<template>
  <Box>
    <div
      class="box box-inner--padding flex items-center justify-between space-x-6"
    >
      <template v-if="selectedClient && !isLoading">
        <div class="min-w-0 text-[0]">
          <Label v-if="showLabels" class="!block" small>
            {{ $t('general.client') }}
          </Label>
          <span class="block truncate text-sm text-gray-900">
            {{ selectedClient.name }}
          </span>
        </div>
        <div class="flex items-center space-x-6">
          <div v-if="showAssignee" class="text-[0]">
            <Label v-if="showLabels" class="!block" small>
              {{ $t('general.assignee') }}
            </Label>

            <UserAvatar
              :user="selectedClient.assignee"
              size="6"
              :has-popover="true"
            />
          </div>

          <div class="text-[0]">
            <Label v-if="showLabels" class="!block" small>
              {{ $t('general.status') }}
            </Label>
            <ClientStatusBadge :client="selectedClient" />
          </div>

          <XMarkIcon
            v-if="!isEditing"
            class="flex size-6 cursor-pointer items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
            @click="emit('clearClient')"
          />
          <Button
            v-else-if="showOpenClientButton"
            :href="openClientLink"
            target="_blank"
            type="secondary"
            class="shrink-0"
            icon-only
            @click="emit('clientLinkOpened')"
          >
            <ArrowTopRightOnSquareIcon class="size-4" aria-hidden="true" />
          </Button>
        </div>
      </template>
      <Spinner v-else colored />
    </div>
  </Box>
</template>

<script lang="ts">
export type ClientBoxClient = {
  _id?: string;
  name?: string;
  assignee?: { _id: string; name: string; picture?: string };
  clientUser?: { _id: string; name: string; email: string };
} & StatusBadgeClient;
</script>

<script lang="ts" setup>
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/20/solid';
import type { StatusBadgeClient } from './status/Badge.vue';

const props = defineProps({
  clientId: {
    type: String,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  showOpenClientButton: {
    type: Boolean,
    default: false,
  },
  showLabels: {
    type: Boolean,
    default: true,
  },
  showAssignee: {
    type: Boolean,
    default: true,
  },
  taskIdentifier: {
    type: String,
    default: undefined,
  },
  linkToClientDashboard: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['clearClient', 'clientLinkOpened']);

const { selectedRollout } = useRolloutRouting();
const { $client } = useNuxtApp();

const selectedClient = ref<ClientBoxClient>();
const isLoading = ref(false);

const rolloutIdentifier = computed(() => {
  if (props.taskIdentifier) {
    const segments = props.taskIdentifier.split('-');

    segments.pop();

    return segments.join('-');
  }

  if (selectedRollout.value) {
    return selectedRollout.value.identifier;
  }

  return '';
});

const openClientLink = computed(() =>
  props.linkToClientDashboard
    ? `/rollout-project/${rolloutIdentifier.value}/rollout?client=${props.clientId}`
    : `/rollout-project/${rolloutIdentifier.value}/inventory/clients/view/${props.clientId}`
);

const fetchClientById = () => {
  if (isLoading.value) {
    return;
  }

  isLoading.value = true;
  $client.client.getById
    .query({
      _id: props.clientId,
    })
    .then((res) => {
      // TODO: Move to Api Service and fix typing (https://rollouter.atlassian.net/browse/RCP-1106)
      selectedClient.value = res.item;
    })
    .catch((err) => {
      useErrorHandler(err);
    })
    .finally(() => {
      isLoading.value = false;
    });
};

fetchClientById();
</script>
